import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface AccuracyReportFiltersProps {
  customFilters: {
    fromPeriod?: string;
    toPeriod?: string;
    direction?: string;
    useVersions?: string;
    displayReportFor?: string;
    detailsOption?: string;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onDirectionChange?: (direction: string) => void;
    onUseVersionsChange?: (version: string) => void;
    onDisplayReportForChange?: (display: string) => void;
    onDetailsOptionChange?: (option: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

// Generate year-quarter options from 2008 to current year
const generateYearQuarterOptions = () => {
  const options: { value: string; label: string; year: string; quarter: string }[] = [];
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const currentYear = new Date().getFullYear();
  
  for (let year = currentYear; year >= 2008; year--) {
    quarters.forEach(quarter => {
      const value = `${year}-${quarter}`;
      const label = `${year} ${quarter}`;
      options.push({
        value,
        label,
        year: year.toString(),
        quarter
      });
    });
  }
  
  return options;
};

const AccuracyReportFilters: React.FC<AccuracyReportFiltersProps> = ({ customFilters }) => {
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    return generateYearQuarterOptions();
  }, [customFilters.yearQuarterOptions]);

  // Anchor elements for dropdown menus
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [useVersionsAnchorEl, setUseVersionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [displayReportForAnchorEl, setDisplayReportForAnchorEl] = React.useState<null | HTMLElement>(null);
  const [detailsOptionAnchorEl, setDetailsOptionAnchorEl] = React.useState<null | HTMLElement>(null);

  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);
  const isUseVersionsMenuOpen = Boolean(useVersionsAnchorEl);
  const isDisplayReportForMenuOpen = Boolean(displayReportForAnchorEl);
  const isDetailsOptionMenuOpen = Boolean(detailsOptionAnchorEl);

  // Handler functions
  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    if (customFilters?.onFromPeriodChange) {
      customFilters.onFromPeriodChange(period);
    }
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    if (customFilters?.onToPeriodChange) {
      customFilters.onToPeriodChange(period);
    }
    handleToPeriodMenuClose();
  };

  const handleUseVersionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setUseVersionsAnchorEl(event.currentTarget);
  };

  const handleUseVersionsMenuClose = () => {
    setUseVersionsAnchorEl(null);
  };

  const handleUseVersionsChange = (version: string) => {
    if (customFilters?.onUseVersionsChange) {
      customFilters.onUseVersionsChange(version);
    }
    handleUseVersionsMenuClose();
  };

  const handleDisplayReportForClick = (event: React.MouseEvent<HTMLElement>) => {
    setDisplayReportForAnchorEl(event.currentTarget);
  };

  const handleDisplayReportForMenuClose = () => {
    setDisplayReportForAnchorEl(null);
  };

  const handleDisplayReportForChange = (display: string) => {
    if (customFilters?.onDisplayReportForChange) {
      customFilters.onDisplayReportForChange(display);
    }
    handleDisplayReportForMenuClose();
  };

  const handleDetailsOptionClick = (event: React.MouseEvent<HTMLElement>) => {
    setDetailsOptionAnchorEl(event.currentTarget);
  };

  const handleDetailsOptionMenuClose = () => {
    setDetailsOptionAnchorEl(null);
  };

  const handleDetailsOptionChange = (option: string) => {
    if (customFilters?.onDetailsOptionChange) {
      customFilters.onDetailsOptionChange(option);
    }
    handleDetailsOptionMenuClose();
  };

  const handleDirectionChange = (direction: string) => {
    if (customFilters?.onDirectionChange) {
      customFilters.onDirectionChange(direction);
    }
  };

  // Label helper functions
  const getFromPeriodLabel = () => {
    if (!customFilters?.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters?.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  const getUseVersionsLabel = () => {
    switch (customFilters?.useVersions) {
      case 'most_recent':
        return 'Most Recent';
      case 'original':
        return 'Original';
      default:
        return 'Most Recent';
    }
  };

  const getDisplayReportForLabel = () => {
    switch (customFilters?.displayReportFor) {
      case 'all_strata':
        return 'All Strata';
      case 'mc_and_format':
        return 'MC and Format';
      case 'format':
        return 'Format';
      default:
        return 'All Strata';
    }
  };

  const getDetailsOptionLabel = () => {
    switch (customFilters?.detailsOption) {
      case 'hide_details':
        return 'Hide Details';
      case 'show_details':
        return 'Show Details';
      default:
        return 'Hide Details';
    }
  };

  const getDirectionLabel = () => {
    return customFilters?.direction || 'Inbound';
  };

  // Direction options
  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Use Versions options
  const useVersionsOptions = [
    { id: 'most_recent', label: 'Most recent eNO1 versions' },
    { id: 'original', label: 'Original eNO1 versions (versions 0)' },
  ];

  // Display Report For options
  const displayReportForOptions = [
    { id: 'all_strata', label: 'All Strata' },
    { id: 'mc_and_format', label: 'MC and Format' },
    { id: 'format', label: 'Format' },
  ];

  // Details options
  const detailsOptions = [
    { id: 'hide_details', label: 'Hide details' },
    { id: 'show_details', label: 'Show details' },
  ];

  return (
    <>
      {/* Direction Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <StyledSegmentedControl>
          {directionOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={customFilters?.direction === option.id}
              onClick={() => handleDirectionChange(option.id)}
              sx={{ 
                color: customFilters?.direction === option.id ? '#ffffff' : '#00B6BA',
                minWidth: '70px'
              }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      </Box>

      {/* From Period Filter */}
      <FilterButton onClick={handleFromPeriodClick}>
        <span className="label">From</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getFromPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={fromPeriodAnchorEl}
        open={isFromPeriodMenuOpen}
        onClose={handleFromPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleFromPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFromPeriodChange(option.value)}
            selected={customFilters?.fromPeriod === option.value}
            sx={{
              fontWeight: customFilters?.fromPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* To Period Filter */}
      <FilterButton onClick={handleToPeriodClick}>
        <span className="label">To</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getToPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={toPeriodAnchorEl}
        open={isToPeriodMenuOpen}
        onClose={handleToPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleToPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleToPeriodChange(option.value)}
            selected={customFilters?.toPeriod === option.value}
            sx={{
              fontWeight: customFilters?.toPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Use Versions Filter */}
      <FilterButton onClick={handleUseVersionsClick}>
        <span className="label">Versions</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getUseVersionsLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={useVersionsAnchorEl}
        open={isUseVersionsMenuOpen}
        onClose={handleUseVersionsMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {useVersionsOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleUseVersionsChange(option.id)}
            selected={customFilters?.useVersions === option.id}
            sx={{
              fontWeight: customFilters?.useVersions === option.id ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Display Report For Filter */}
      <FilterButton onClick={handleDisplayReportForClick}>
        <span className="label">Display For</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getDisplayReportForLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={displayReportForAnchorEl}
        open={isDisplayReportForMenuOpen}
        onClose={handleDisplayReportForMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '200px',
          },
        }}
      >
        {displayReportForOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleDisplayReportForChange(option.id)}
            selected={customFilters?.displayReportFor === option.id}
            sx={{
              fontWeight: customFilters?.displayReportFor === option.id ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Details Option Filter */}
      <FilterButton onClick={handleDetailsOptionClick}>
        <span className="label">Details</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getDetailsOptionLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={detailsOptionAnchorEl}
        open={isDetailsOptionMenuOpen}
        onClose={handleDetailsOptionMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '200px',
          },
        }}
      >
        {detailsOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleDetailsOptionChange(option.id)}
            selected={customFilters?.detailsOption === option.id}
            sx={{
              fontWeight: customFilters?.detailsOption === option.id ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('HTML')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('PDF')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'PDF'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('XLS')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'XLS'}
          </Typography>
        </StyledActionButton>
      </Box>
    </>
  );
};

export default AccuracyReportFilters; 